import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { activityReportsApi, workplanActivitiesApi, workplansApi } from '../../api/planningApi';
import { CreateNewModal } from '../../component/Planning/PARCreateModal';
import { usePlanningReferenceOptions } from '../../component/Planning/usePlanningReferenceOptions';
import { SubModulePanel } from '../../component/shared/CoreCreateModal';
import RegistryCustomFields from '../../component/Admin/FoundationalTables/FormRegistry/RegistryCustomFields';
import { RegistryFormScope } from '../../component/Admin/FoundationalTables/FormRegistry/registryFieldOverrides';

const ROUTES = {
  workplans: '/planning-reporting/workplans',
  reports: '/planning-reporting/reports',
};

const ALLOWED_TABS = ['workplans', 'reports'];

const TAB_LABELS = {
  workplans: 'Workplan & Activities',
  reports: 'Activity Report',
};

const SUB_MODULE_OPTIONS = ALLOWED_TABS.map((value) => ({
  value,
  label: TAB_LABELS[value] ?? value,
}));

function useCollectionRows(api, enabled) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!enabled) {
      setRows([]);
      return undefined;
    }

    let active = true;
    api.listAll({ per_page: 1000, page: 1 })
      .then((result) => {
        if (!active) return;
        setRows(Array.isArray(result?.items) ? result.items : []);
      })
      .catch(() => {
        if (active) setRows([]);
      });

    return () => {
      active = false;
    };
  }, [api, enabled]);

  return rows;
}

export default function PlanningReportingAddRecord({ viewOnly = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { uuid } = useParams();
  const mode = uuid ? (viewOnly ? 'view' : 'edit') : 'create';
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const visibleTabs = useMemo(
    () => (user?.roleId === 'R05' ? ['workplans'] : ALLOWED_TABS),
    [user?.roleId]
  );

  const activeTab = useMemo(() => {
    const requested = String(searchParams.get('tab') ?? '').trim();
    return visibleTabs.includes(requested)
      ? requested
      : 'workplans';
  }, [searchParams, visibleTabs]);
  const subModuleOptions = useMemo(
    () => SUB_MODULE_OPTIONS.filter((option) => visibleTabs.includes(option.value)),
    [visibleTabs]
  );

  // Activities are needed both for Reports (Parent Activity dropdown) and
  // for Workplans (existing-linked-activities list + remaining budget calc
  // on the embedded repeatable Activity section).
  const activities = useCollectionRows(workplanActivitiesApi, activeTab === 'reports' || activeTab === 'workplans');
  // Workplans drive the report form's approved-activity list (Parent
  // Activity) and its Reform Plan picker; reports drive each workplan's
  // derived Completion Percent.
  const workplans = useCollectionRows(workplansApi, activeTab === 'reports' || activeTab === 'workplans');
  const reports = useCollectionRows(activityReportsApi, activeTab === 'workplans');

  const referenceOptions = usePlanningReferenceOptions({
    workplans,
    activities,
    reports,
    currentUserUuid: user?.uuid ?? '',
    currentUserLabel: user?.name ?? '',
    currentInstitutionUuid: user?.institutionUuid ?? '',
    currentInstitutionLabel: user?.institution ?? '',
  });

  const context = useMemo(() => ({
    referenceOptions,
    activities,
    workplans,
    reports,
    currentUserUuid: user?.uuid ?? '',
    currentUserLabel: user?.name ?? '',
    currentInstitutionUuid: user?.institutionUuid ?? '',
    currentInstitutionLabel: user?.institution ?? '',
  }), [activities, workplans, reports, referenceOptions, user?.institution, user?.institutionUuid, user?.name, user?.uuid]);

  const handleClose = useCallback(() => {
    navigate(ROUTES[activeTab] ?? ROUTES.workplans, { replace: true });
  }, [activeTab, navigate]);

  // ── View/Edit mode: load the existing record ────────────────────────────
  // Prefer the already-normalized row handed over via navigation state (the
  // exact object the table already fetched from the live API) — this is
  // known-complete. Fall back to a fresh live GET only when the page is
  // opened without that state (e.g. a direct link or a refresh).
  const [row, setRow] = useState(null);
  const [loadingRow, setLoadingRow] = useState(Boolean(uuid));
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!uuid) { setRow(null); setLoadingRow(false); return; }
    const seedRow = location.state?.row;
    if (seedRow && (seedRow.uuid === uuid || seedRow.id === uuid)) {
      setRow(seedRow);
      setLoadingRow(false);
      return;
    }
    const recordApi = activeTab === 'reports' ? activityReportsApi : workplansApi;
    setLoadingRow(true);
    setLoadError('');
    recordApi.get(uuid)
      .then((r) => setRow(r))
      .catch((e) => setLoadError(e?.response?.data?.message ?? e.message ?? 'Failed to load record.'))
      .finally(() => setLoadingRow(false));
  }, [uuid, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Edit mode: save handlers — ported verbatim from each tab's handleSave
  // in PlanningTabsLive.jsx so the update payload shape is identical. ──────
  const handleWorkplanSave = useCallback(async (payload) => {
    const { activities: newActivities, ...workplanPayload } = payload;
    await workplansApi.update(uuid, workplanPayload);

    // Only newly-added rows reach here — activities already linked to this
    // workplan are shown read-only and aren't resubmitted.
    const entriesToCreate = (newActivities ?? []).filter((a) => a.activityTitle || a.category);
    if (entriesToCreate.length > 0) {
      const failedLabels = [];
      for (const activity of entriesToCreate) {
        try {
          await workplanActivitiesApi.create({
            workplan_uuid: uuid,
            title: activity.activityTitle,
            activity_title: activity.activityTitle,
            category: activity.category,
            budget_ngn: activity.budgetNgn,
            target_beneficiaries: activity.targetBeneficiaries,
            beneficiary_class: activity.beneficiaryClass,
            climate_screening: activity.climateScreening,
            target_facility_id: activity.targetFacilityId,
            status: 'Planned',
            activity_status: 'Planned',
          });
        } catch {
          failedLabels.push(activity.activityTitle || 'Activity');
        }
      }
      if (failedLabels.length > 0) {
        return { warning: `Workplan saved, but ${failedLabels.length} new activity(ies) could not be saved (${failedLabels.join(', ')}).` };
      }
    }
    return undefined;
  }, [uuid]);

  const handleReportSave = useCallback(async (payload) => {
    await activityReportsApi.update(uuid, {
      workplan_activity_uuid: payload.workplanActivityUuid || payload.parentActivityId,
      parent_activity_id: payload.parentActivityId || payload.workplanActivityUuid,
      activity_type: payload.activityType,
      ppp_id: payload.pppId,
      reporting_period: payload.reportingPeriod,
      actual_start_date: payload.actualStartDate,
      execution_status: payload.executionStatus,
      actual_expenditure_ngn: payload.actualExpenditureNgn || payload.expenditure,
      outputs_target: payload.outputsTarget,
      outputs_achieved: payload.outputsAchieved,
      submission_date: payload.submissionDate,
      reported_by_uuid: payload.reportedByUuid,
      variance_explanation: payload.varianceExplanation || payload.varianceExpl,
      attachments: payload.attachments,
      attachments_file_name: payload.attachmentsFileName,
      facility_assessment_ref: payload.facilityAssessmentRef,
    });
  }, [uuid]);

  const handleUpdate = useCallback((payload) => (
    activeTab === 'reports' ? handleReportSave(payload) : handleWorkplanSave(payload)
  ), [activeTab, handleReportSave, handleWorkplanSave]);

  const handleSubModuleChange = useCallback((value) => {
    if (!visibleTabs.includes(value)) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', value);
      return next;
    }, { replace: true });
  }, [setSearchParams, visibleTabs]);

  const modulePanel = (
    <SubModulePanel
      moduleName="Planning & Reporting"
      moduleDescription="Select a sub module to open the matching planning or reporting record form."
      subModuleOptions={subModuleOptions}
      subModule={activeTab}
      onSubModuleChange={handleSubModuleChange}
      disabled={subModuleOptions.length === 1}
    />
  );

  const handleSubmit = useCallback(async (payload) => {
    if (activeTab === 'reports') {
      await activityReportsApi.create({
        workplan_activity_uuid: payload.workplanActivityUuid || payload.parentActivityId,
        parent_activity_id: payload.parentActivityId || payload.workplanActivityUuid,
        activity_type: payload.activityType,
        ppp_id: payload.pppId,
        reporting_period: payload.reportingPeriod,
        actual_start_date: payload.actualStartDate,
        execution_status: payload.executionStatus,
        actual_expenditure_ngn: payload.actualExpenditureNgn || payload.expenditure,
        outputs_target: payload.outputsTarget,
        outputs_achieved: payload.outputsAchieved,
        submission_date: payload.submissionDate,
        reported_by_uuid: payload.reportedByUuid || user?.uuid,
        variance_explanation: payload.varianceExplanation || payload.varianceExpl,
        attachments: payload.attachments,
        attachments_file_name: payload.attachmentsFileName,
        facility_assessment_ref: payload.facilityAssessmentRef,
      });
      return undefined;
    }

    // Workplan & Activities — a single DCIC-11 instrument: create the
    // workplan, then its repeatable activity rows. A per-activity failure
    // doesn't roll back the workplan (matches the Actor+Qualification
    // partial-failure pattern) — it's surfaced as a warning instead.
    const { activities: newActivities, ...workplanPayload } = payload;
    const created = await workplansApi.create({
      ...workplanPayload,
      institution_uuid: workplanPayload.institution_uuid || workplanPayload.institutionUuid || user?.institutionUuid,
      plan_owner_uuid: workplanPayload.planOwnerUuid || user?.uuid,
    });

    const workplanUuid = created?.uuid ?? created?.id ?? '';
    const entriesToCreate = (newActivities ?? []).filter((a) => a.activityTitle || a.category);
    if (workplanUuid && entriesToCreate.length > 0) {
      const failedLabels = [];
      for (const activity of entriesToCreate) {
        try {
          await workplanActivitiesApi.create({
            workplan_uuid: workplanUuid,
            title: activity.activityTitle,
            activity_title: activity.activityTitle,
            category: activity.category,
            budget_ngn: activity.budgetNgn,
            target_beneficiaries: activity.targetBeneficiaries,
            beneficiary_class: activity.beneficiaryClass,
            climate_screening: activity.climateScreening,
            target_facility_id: activity.targetFacilityId,
            status: 'Planned',
            activity_status: 'Planned',
          });
        } catch {
          failedLabels.push(activity.activityTitle || 'Activity');
        }
      }
      if (failedLabels.length > 0) {
        return { warning: `Workplan created, but ${failedLabels.length} activity(ies) could not be saved (${failedLabels.join(', ')}).` };
      }
    }
    return undefined;
  }, [activeTab, user?.institutionUuid, user?.uuid]);

  return (
    <div className="min-h-screen bg-[#f7f7f7] px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        {loadingRow && (
          <div className="mb-4 rounded-lg border border-amber-100 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700">
            Loading record…
          </div>
        )}
        {loadError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {loadError}
          </div>
        )}

        {(mode === 'create' || row) && (
          <RegistryFormScope moduleKey="planning-reporting" submoduleKey={activeTab}>
            <>
              <RegistryCustomFields moduleKey="planning-reporting" submoduleKey={activeTab} />
              <CreateNewModal
                key={activeTab}
                tab={activeTab}
                context={context}
                onSubmit={mode === 'create' ? handleSubmit : handleUpdate}
                onClose={handleClose}
                inline
                modulePanel={modulePanel}
                mode={mode}
                row={mode === 'create' ? null : row}
                onEdit={() => navigate(`/planning-reporting/edit/${uuid}?tab=${activeTab}`, { state: { row } })}
                canEdit={true}
              />
            </>
          </RegistryFormScope>
        )}
      </div>
    </div>
  );
}
